import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import cn from 'classnames'
import { FormControl, Input, InputLabel, IconButton } from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import ClearIcon from '@material-ui/icons/Clear'
import isEqual from 'lodash/isEqual'
import messages from 'routes/App/Apps/LogbookForm/translate'
import { showNotice } from 'reducers/notice'
import NoticeHelper from 'helpers/notice'
import imageCompression from 'browser-image-compression'
import { uploadFiles } from 'reducers/common/files/reducer'
import withPromise from 'helpers/withPromise'
import { MY_JOBS_UPLOAD_MULTIPLE_FILES } from 'constants/apiRouting'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import ImageModal from 'components/common/Modals/ImageModal'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import { ReactComponent as DownloadIcon } from 'assets/img/download-icon.svg'
import { ReactComponent as CloseIcon } from 'assets/img/close-icon.svg'
import { ReactComponent as ViewMoreIcon } from 'assets/img/view-more-icon.svg'
import { ReactComponent as RemoveIcon } from 'assets/img/remove-icon.svg'
import { ReactComponent as UploadIcon } from 'assets/img/upload-icon.svg'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import ReactImageAppear from 'react-image-appear'
import { JobFieldProps } from './JobFields.types'

interface FileObject {
  id: number
  uri: string
  s3DownloadUri?: string
  originalName: string
  mimeType: string
  mime_type?: string
  original_name?: string
  fileName?: string
  s3_download_uri?: string
  downloadUri?: string
  file?: {
    id?: number
  }
}

interface UploadErrorResponse {
  error: boolean
}

interface JobMultipleFilesInputProps extends JobFieldProps {
  inputData: {
    evtInputId: number
    value: Array<FileObject> | FileObject | null
  }
  showNotice: (notice: { notice: { message: string; type: string } }) => void
  uploadFiles: (payload: {
    file: FormData
    partUrl: string
    formatMessage: (message: { id: string }) => string
  }) => unknown
  // eslint-disable-next-line react/require-default-props
  onJobTaskFocusedInput?: (value: boolean) => void | undefined
}

const JobMultipleFilesInput = (
  props: JobMultipleFilesInputProps & WrappedComponentProps,
) => {
  const {
    index,
    disabled,
    className,
    isTaskCompleted,
    isFocusedInput,
    inputInfo,
    inputData,
    showNotice,
    uploadFiles,
    onJobTaskAutoSave,
    onJobTaskFocusedInput = () => {},
    intl: { formatMessage },
  } = props
  const { title, required, id: inputIdFromInfo } = inputInfo
  const { evtInputId } = inputData

  const [value, setValue] = useState<Array<FileObject>>([])
  const [oldValue, setOldValue] = useState<Array<FileObject>>([])
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null,
  )
  const [fileIndexToDelete, setFileIndexToDelete] = useState<number | null>(
    null,
  )
  const [evtTaskInputId, setEvtTaskInputId] = useState<string | number>('')
  const [isUpdating, setIsUpdating] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const normalizeValue = useCallback(
    (rawValue: Array<FileObject> | FileObject | null): Array<FileObject> => {
      if (!rawValue) {
        return []
      }

      if (Array.isArray(rawValue)) {
        return rawValue
      }

      return [rawValue]
    },
    [],
  )

  const normalizeFileObject = useCallback(
    (file: FileObject): FileObject => ({
      ...file,
      mimeType: file?.mimeType || file?.mime_type || '',
      originalName:
        file?.originalName || file?.original_name || file?.fileName || '',
      s3DownloadUri:
        file?.s3DownloadUri || file?.s3_download_uri || file?.downloadUri,
    }),
    [],
  )

  const normalizeUploadedFiles = useCallback(
    (
      response: FileObject[] | { files?: FileObject[] } | unknown,
    ): FileObject[] => {
      if (Array.isArray(response)) {
        return response.map(normalizeFileObject)
      }

      if (
        response &&
        typeof response === 'object' &&
        Array.isArray((response as { files?: FileObject[] }).files)
      ) {
        return (response as { files: FileObject[] }).files.map(
          normalizeFileObject,
        )
      }

      return []
    },
    [normalizeFileObject],
  )

  const getMimeType = useCallback(
    (file: FileObject): string => file?.mimeType || file?.mime_type || '',
    [],
  )

  const isImage = useCallback(
    (file: FileObject): boolean => getMimeType(file).includes('image'),
    [getMimeType],
  )

  const getFileId = useCallback((file: FileObject): number | undefined => {
    if (typeof file?.id === 'number') {
      return file.id
    }

    return file?.file?.id
  }, [])

  useEffect(() => {
    const normalizedValue = normalizeValue(inputData.value)
    setValue(normalizedValue)
    setOldValue(normalizedValue)
    setEvtTaskInputId(inputData.evtInputId)
  }, [inputInfo, inputData, normalizeValue])

  const setResultId = useCallback(({ id }: { id: number }) => {
    setEvtTaskInputId(id)
  }, [])

  const onInputBlur = useCallback(
    (nextValue?: Array<FileObject>) => {
      const currentValue = nextValue || value

      if (!isEqual(currentValue, oldValue)) {
        setIsUpdating(true)

        const fileIds = currentValue
          .map(file => getFileId(file))
          .filter((fileId): fileId is number => typeof fileId === 'number')

        onJobTaskAutoSave(
          {
            evtInputId: evtInputId || evtTaskInputId,
            result: fileIds.length > 0 ? fileIds : null,
            input: inputIdFromInfo,
            file: currentValue,
            setResultId,
          },
          () => {
            setIsUpdating(false)
            setOldValue(currentValue)
          },
        )
      }
    },
    [
      value,
      oldValue,
      getFileId,
      onJobTaskAutoSave,
      evtInputId,
      evtTaskInputId,
      inputIdFromInfo,
      setResultId,
    ],
  )

  const openDeleteConfirm = useCallback(
    (indexToRemove: number) => (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setFileIndexToDelete(indexToRemove)
    },
    [],
  )

  const closeDeleteConfirm = useCallback(() => {
    setFileIndexToDelete(null)
  }, [])

  const onConfirmRemoveFile = useCallback(() => {
    if (fileIndexToDelete === null) {
      return
    }

    const newValue = value.filter((_, i) => i !== fileIndexToDelete)
    setValue(newValue)
    setFileIndexToDelete(null)
    onInputBlur(newValue)
  }, [fileIndexToDelete, value, onInputBlur])

  const handleModalChange = useCallback((index: number | null = null) => {
    setIsOpen(index !== null)
    setSelectedFileIndex(index)
  }, [])

  const handleDownload = useCallback(
    (file: FileObject) => async (e: React.MouseEvent) => {
      e.preventDefault()
      const link = document.createElement('a')
      link.download = file.originalName || 'file'
      link.href = file.s3DownloadUri || file.uri

      if (ifWindowWidthLessThan(TABLET_BREAKPOINT)) {
        link.target = '_blank'
      }

      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    [],
  )

  const onChangeFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputElement = e.currentTarget
      const { files } = inputElement

      if (!files || files.length === 0) {
        return false
      }

      onJobTaskFocusedInput(true)

      const fileArray = Array.from(files)

      try {
        const options = {
          initialQuality: 0.9,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        }
        const fileForm = new FormData()
        const preparedFiles = await Promise.all(
          fileArray.map(async file => {
            if (file.type.startsWith('image/')) {
              const compressedFile = await imageCompression(file, options)

              return new File([compressedFile], encodeURI(file.name))
            }

            return file
          }),
        )

        preparedFiles.forEach(file => {
          fileForm.append('files[]', file)
        })

        const response = (await withPromise(uploadFiles, {
          file: fileForm,
          partUrl: MY_JOBS_UPLOAD_MULTIPLE_FILES,
          formatMessage,
        })) as UploadErrorResponse | FileObject[] | { files?: FileObject[] }

        if ((response as UploadErrorResponse)?.error) {
          throw new Error('Upload failed')
        }

        const successfulUploads = normalizeUploadedFiles(response)

        if (!successfulUploads.length) {
          return false
        }

        setValue(prevState => {
          const mergedById = new Map<number | string, FileObject>()

          prevState.forEach(file => {
            const fileId = getFileId(file) ?? `${file.uri}-${file.originalName}`
            mergedById.set(fileId, file)
          })

          successfulUploads.forEach(file => {
            const fileId = getFileId(file) ?? `${file.uri}-${file.originalName}`
            mergedById.set(fileId, file)
          })

          const updatedValue = Array.from(mergedById.values())
          onInputBlur(updatedValue)

          return updatedValue
        })
      } catch (error) {
        console.error('Error while compressing/uploading files:', error)
        showNotice({
          notice: NoticeHelper.createErrorObj(
            formatMessage(messages.fileNotUploadMessage),
          ),
        })
      }

      inputElement.value = ''

      return true
    },
    [
      onJobTaskFocusedInput,
      uploadFiles,
      formatMessage,
      normalizeUploadedFiles,
      onInputBlur,
      showNotice,
      getFileId,
    ],
  )

  const inputId = `jobMultipleFiles${index}`
  const fileInputKey = `${evtTaskInputId}_${inputId}`
  const drawAsterisk = required ? '*' : ''
  const isInputDisabled = disabled || isUpdating || isFocusedInput
  const hasFiles = value.length > 0

  const canAddMoreFiles = !disabled && !isTaskCompleted

  const triggerFilePicker = useCallback(() => {
    if (!canAddMoreFiles || isUpdating) {
      return
    }

    fileInputRef.current?.click()
  }, [canAddMoreFiles, isUpdating])

  const imageFiles = useMemo(
    () =>
      value
        .map((file, originalIndex) => ({ file, originalIndex }))
        .filter(({ file }) => isImage(file)),
    [value, isImage],
  )

  const nonImageFiles = useMemo(
    () =>
      value
        .map((file, originalIndex) => ({ file, originalIndex }))
        .filter(({ file }) => !isImage(file)),
    [value, isImage],
  )

  const selectedFile =
    selectedFileIndex !== null ? value[selectedFileIndex] : null
  const selectedFileIsImage = selectedFile ? isImage(selectedFile) : false
  const showImageAddTile = hasFiles && imageFiles.length >= 1
  const showDocumentAddRow =
    hasFiles && nonImageFiles.length >= 1 && imageFiles.length === 0

  return (
    <FormControl fullWidth className={className}>
      <span className="job-field-label">
        {title} {drawAsterisk}
      </span>
      <InputLabel htmlFor={inputId} className="job-field-label job-file-title">
        {!hasFiles && (
          <div
            className={cn('job-field-input', 'no-file', {
              disabled: isInputDisabled,
            })}>
            <UploadIcon className="job-field-icon" />
            {formatMessage(messages.uploadPdfPngJpg)}
          </div>
        )}
        {hasFiles && (
          <div className="job-multiple-files-preview">
            {Boolean(imageFiles.length) && (
              <div className="job-multiple-files-images">
                {imageFiles.map(({ file, originalIndex }) => (
                  <div
                    key={`${file.id}-${originalIndex}`}
                    className="job-multiple-files-image-item">
                    <div className={cn('image-preview', 'small')}>
                      <ReactImageAppear
                        src={file.uri}
                        animationDuration="1s"
                        placeholderStyle={{
                          backgroundColor: 'transparent',
                        }}
                      />
                      <div className="btn-wrap">
                        <IconButton
                          onClick={() => handleModalChange(originalIndex)}>
                          <ViewMoreIcon />
                        </IconButton>
                        <IconButton
                          className="download-button"
                          onClick={handleDownload(file)}>
                          <DownloadIcon />
                        </IconButton>
                      </div>
                      {!isTaskCompleted && (
                        <IconButton
                          className="delete-button"
                          onClick={openDeleteConfirm(originalIndex)}>
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </div>
                    <span className="job-multiple-files-image-title">
                      {decodeURI(encodeURI(file.originalName))}
                    </span>
                  </div>
                ))}
                {showImageAddTile && (
                  <button
                    type="button"
                    className={cn('job-multiple-files-add-tile', {
                      disabled: !canAddMoreFiles || isUpdating,
                    })}
                    disabled={!canAddMoreFiles || isUpdating}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      triggerFilePicker()
                    }}
                    aria-label={formatMessage({ id: 'add' })}>
                    <span className="job-multiple-files-add-icon">+</span>
                  </button>
                )}
              </div>
            )}

            {Boolean(nonImageFiles.length) && (
              <div className="job-multiple-files-list">
                {nonImageFiles.map(({ file, originalIndex }) => (
                  <div
                    key={`${file.id}-${originalIndex}`}
                    className={cn('job-field-input', 'file')}>
                    <DescriptionIcon className="job-field-icon" />
                    <a
                      href={file.uri}
                      title={decodeURI(encodeURI(file.originalName))}
                      target="_blank"
                      rel="noopener noreferrer">
                      {decodeURI(encodeURI(file.originalName))}
                    </a>
                    {!isTaskCompleted && (
                      <ClearIcon
                        className="job-field-icon job-field-clear-icon"
                        onClick={openDeleteConfirm(originalIndex)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {showDocumentAddRow && (
              <button
                type="button"
                className={cn('job-multiple-files-add-documents', {
                  disabled: !canAddMoreFiles || isUpdating,
                })}
                disabled={!canAddMoreFiles || isUpdating}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  triggerFilePicker()
                }}>
                <span className="job-multiple-files-add-icon">+</span>
                <span>{formatMessage({ id: 'add' })}</span>
              </button>
            )}
          </div>
        )}
        <Input
          key={fileInputKey}
          id={inputId}
          inputRef={fileInputRef}
          type="file"
          onChange={onChangeFile}
          className="job-field-file-input"
          inputProps={{
            accept:
              'image/jpeg,image/png,image/jpg,image/tiff,.tif,.tiff,application/pdf,application/x-pdf',
            multiple: true,
          }}
          disabled={isInputDisabled}
        />
      </InputLabel>
      {selectedFile && selectedFileIsImage && (
        <ImageModal open={isOpen} onClose={() => handleModalChange(null)}>
          <div className="modal-content">
            <div className="image-wrap">
              <ReactImageAppear
                src={selectedFile.uri}
                animationDuration="2s"
                placeholderStyle={{ backgroundColor: 'transparent' }}
              />
            </div>
            <div className="btn-wrap">
              <IconButton onClick={() => handleModalChange(null)}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </ImageModal>
      )}
      <ConfirmDialog
        // @ts-expect-error ConfirmDialog is JS file
        open={fileIndexToDelete !== null}
        title={formatMessage(messages.delete)}
        message={formatMessage(messages.deleteFileConfirmMessage)}
        className="dialog-primary-custom"
        onClose={closeDeleteConfirm}
        onConfirmAction={onConfirmRemoveFile}
        cancel={{
          color: 'default',
          variant: 'outlined',
          customText: formatMessage(messages.cancel),
        }}
        confirm={{
          color: 'scales-secondary',
          customText: formatMessage(messages.delete),
        }}
      />
    </FormControl>
  )
}

export default connect(null, {
  showNotice,
  uploadFiles,
})(injectIntl(JobMultipleFilesInput))
